import { FC, useState, useCallback, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { DrawerChooser, CarouselMaterialTitle } from 'src/atoms';
import { ErrorContext } from 'src/context/ErrorContext';
import { MaterialsCarousel } from 'src/molecules';
import { getNFTBackgroundsMock as getNFTBackgrounds } from 'src/services';
import { NFTBackgroundsResult } from 'src/services/NFTService/NFTService.types';

import { DrawerAvatarBackgroundChooserProps, BackgroundItemProps } from './DrawerAvatarBackgroundChooser.types';

const dataToMaterialItem = (data: NFTBackgroundsResult[]): BackgroundItemProps[] => {
  return data.map((material, idx) => {
    return {
      id: `${material.material_level_tier}_${idx}`,
      img: material.image,
      level: parseInt(material.material_level_tier, 10),
      levelName: material.material_level_name,
    };
  });
};

const DrawerAvatarSkinChooser: FC<DrawerAvatarBackgroundChooserProps> = ({ isOpen, onClose, onApply }) => {
  const { t } = useTranslation('app');
  const { throwServerError } = useContext(ErrorContext);

  const [items, setItems] = useState<BackgroundItemProps[]>();
  const [selected, setSelected] = useState<string | undefined>();
  const [isChanged, setIsChanged] = useState<boolean>(false);

  const handleChange = (itemId: string | undefined) => {
    setSelected(itemId);
    setIsChanged(true);
  };

  const readData = useCallback(() => {
    getNFTBackgrounds()
      .then(({ data }) => {
        const resultItems = dataToMaterialItem(data.results);
        setItems(resultItems);
      })
      .catch((error) => {
        if (error.response?.status >= 500) throwServerError();
      });
  }, [throwServerError]);

  useEffect(() => {
    if (isOpen) readData();
  }, [readData, isOpen]);

  const selectedItem = items?.find((item) => item.id === selected);

  const handlerApply = () => onApply && onApply(selectedItem?.level || 0);

  return (
    <DrawerChooser
      isOpen={isOpen}
      onClose={onClose}
      isChanged={isChanged}
      onApply={handlerApply}
      titleComponent={<CarouselMaterialTitle title={t('avatar.menu.background')} />}
    >
      <MaterialsCarousel items={items} selected={selected} onSelect={(id) => handleChange(id)} />
    </DrawerChooser>
  );
};

export default DrawerAvatarSkinChooser;
